import { memo, useCallback, useEffect, useMemo } from "react";
import { useSnapshot } from "valtio";
import { DndContext } from "@dnd-kit/core";
import CatalogMain from "./components/CatalogMain";
import LeftPanel from "./components/LeftPanel";
import { contentCatalogStore } from "./contentCatalogStore";
import { resolvedAdStore } from "@/store/resolvedAdStore";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { CoreService } from "@services/core/CoreService";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { contentCatalogEvents } from "./contentCatalogEvents";
import { buildAllContentSearchTree } from "./contentCatalogSearch";
import ResizableSplitView from "@components/ResizableSplitView/ResizableSplitView";
import Loader from "src/components/common/loader/loader";
import { Ad } from "@models/Ad.model";
import type { CatalogSection } from "./contentCatalogDnd";

export type CatalogMenuAction = {
    label: string;
    onSelect: () => void;
    danger?: boolean;
    disabled?: boolean;
};

export type AdMenuItemsFn = (ad: Ad, parentFolderId: number, section: CatalogSection) => CatalogMenuAction[];
export type FolderMenuItemsFn = (folder: ContentCatalogFolder, section: CatalogSection) => CatalogMenuAction[];

export interface ContentCatalogProps {
    organizationId: number;
    adMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    collectionMenuItems?: FolderMenuItemsFn;
    collectionAdMenuItems?: AdMenuItemsFn;
    onAdClick?: (ad: Ad) => void;
    onFolderClick?: (folder: ContentCatalogFolder) => void;
    onCreateRootFolder?: () => void;
    onCreateRootCollection?: () => void;
    enableDnd?: boolean;
    isDragging?: boolean;
    showCollections?: boolean;
}

export default memo(function ContentCatalog({
    organizationId,
    adMenuItems,
    folderMenuItems,
    collectionMenuItems,
    collectionAdMenuItems,
    onAdClick,
    onCreateRootFolder,
    onCreateRootCollection,
    enableDnd = false,
    isDragging = false,
    showCollections = true,
}: ContentCatalogProps) {
    const snapStore = useSnapshot(contentCatalogStore);
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const coreService = useMemo(() => new CoreService(), []);

    const loadCatalogFromApi = useCallback(async (signal: AbortSignal) => {
        // Reset store to prevent stale data flash on remount
        contentCatalogStore.loading = true;
        contentCatalogStore.organizationId = organizationId;
        contentCatalogStore.folders = [];
        contentCatalogStore.collections = [];
        resolvedAdStore.byId = {};
        contentCatalogStore.query = "";
        contentCatalogStore.selectedId = "__all__";

        try {
            const promises: [Promise<ContentCatalogFolder[]>, Promise<ContentCatalogFolder[]>, Promise<any>] = [
                showCollections ? ccService.getFolders(organizationId, undefined, true, "collection", signal) : Promise.resolve([]),
                ccService.getFolders(organizationId, undefined, true, "folder", signal),
                // Unscoped: the filter taxonomy is the same on every catalog (MPN-1579).
                coreService.getTagGroups(undefined, signal),
            ];
            const [collections, folders, tagGroups] = await Promise.all(promises);
            // Requests that already landed before the abort must not publish either.
            if (signal.aborted) return;

            contentCatalogStore.folders = folders;
            contentCatalogStore.collections = collections;
            contentCatalogStore.tagGroups = tagGroups;
            contentCatalogEvents.send_event("CONTENT_LOADED");
        } finally {
            // Clear on failure too, so a failed load shows the empty state
            // rather than a loader that never goes away. A superseded load leaves
            // the flag alone — the load that replaced it owns it now.
            if (!signal.aborted) contentCatalogStore.loading = false;
        }
    }, [ccService, coreService, organizationId, showCollections]);

    useEffect(() => {
        // Switching organizations must cancel the in-flight load, otherwise a slow
        // first response resolves last and overwrites the newly selected org's data.
        const controller = new AbortController();
        loadCatalogFromApi(controller.signal).catch((error) => {
            if (controller.signal.aborted) return;
            console.error("Failed to load content catalog:", error);
        });
        return () => controller.abort();
    }, [loadCatalogFromApi]);

    useEffect(() => {
        const query = (contentCatalogStore.query || "").trim();
        contentCatalogStore.folderSearch = buildAllContentSearchTree(
            contentCatalogStore.folders as ContentCatalogFolder[], query
        );
    }, [snapStore.query, snapStore.folders]);

    const content = (
        <div className={`cc4-root${isDragging ? " cc4-root--dragging" : ""}`}>
            {snapStore.loading && (
                <div className="cc4-loading">
                    <Loader />
                </div>
            )}
            <ResizableSplitView
                className="cc4-layout"
                dividerSize={0}
                left={
                    <LeftPanel
                        adMenuItems={adMenuItems}
                        folderMenuItems={folderMenuItems}
                        collectionMenuItems={collectionMenuItems}
                        collectionAdMenuItems={collectionAdMenuItems}
                        onAdClick={onAdClick}
                        onCreateRootFolder={onCreateRootFolder}
                        onCreateRootCollection={onCreateRootCollection}
                        enableDnd={enableDnd}
                    />
                }
                right={
                    <CatalogMain
                        organizationId={organizationId}
                        adMenuItems={adMenuItems}
                        collectionAdMenuItems={collectionAdMenuItems}
                        folderMenuItems={folderMenuItems}
                        collectionMenuItems={collectionMenuItems}
                        onAdClick={onAdClick}
                        enableDnd={enableDnd}
                    />
                }
                storageKey="contentCatalog.leftPanel.width"
            />
        </div>
    );

    // When DnD is disabled, provide a bare DndContext so hooks don't error
    if (!enableDnd) {
        return <DndContext>{content}</DndContext>;
    }

    return content;
});
